import { AxiosResponse } from 'axios'

import * as Diagnostics from './dom/diagnostics'
import ResponseError, { ApiError } from '../models/ResponseError'
import DirectoryEntry from '../models/DirectoryEntry'
import { PageDto } from '../models/Pagination'
import TableControl from '../models/TableControl'

/**
 * This type is similar to ApiModel<T>, but allows types that are not api models.
 * This description includes the User model, which does not conform to the ApiModel type.
 * This seperation also makes it easier to work with DTOs seperate from the model,
 *  because we are not inferring the DTO type from T in ApiModel<T>.
 */
declare type Model<T, D> = new (data: D) => T

export const ok = (response: AxiosResponse) => response.status < 400

export const throwIfResponseNotOk = (response: AxiosResponse) => {
    if (ok(response)) return
    Diagnostics.append('API', response.data)
    // When the API errors, the error is the body of the response in place of a data payload.
    const error = response.data as ApiError
    throw new ResponseError(error)
}

export const mapModel = <T, D>(data: D, model: Model<T, D>): T => new model(data)

export const mapModelList = <T, D>(data: D[], model: Model<T, D>): T[] =>
    data.map((d) => new model(d))

export const mapStructureModel = <T, D, E extends DirectoryEntry>(
    data: D,
    entry: E | undefined,
    model: new (data: D, entry?: E) => T
): T => new model(data, entry)

export const mapPaginatedTableModel = <T, D, P extends PageDto<D>>(
    data: P,
    model: Model<T, D>
): TableControl<T, D> => new TableControl<T, D>(data, model)

/*
 * These functions are compositions of the functions above for prettier and more consice use.
 */

export const mapReadResponse = <T, D>(response: AxiosResponse<D>, model: Model<T, D>): T => {
    throwIfResponseNotOk(response)
    return mapModel(response.data, model)
}

export const mapReadListResponse = <T, D>(
    response: AxiosResponse<D[]>,
    model: Model<T, D>
): T[] => {
    throwIfResponseNotOk(response)
    return mapModelList(response.data, model)
}

export const mapReadStructureResponse = <T, D>(
    response: AxiosResponse<D>,
    entry: DirectoryEntry | undefined,
    model: Model<T, D>
) => {
    throwIfResponseNotOk(response)
    return mapStructureModel(response.data, entry, model)
}

export const mapReadTableResponse = <T, D, P extends PageDto<D>>(
    response: AxiosResponse<P>,
    current: TableControl<T, D>,
    model: Model<T, D>
) => {
    throwIfResponseNotOk(response)
    const table = mapPaginatedTableModel(response.data, model)
    // NOTE: Must restore table control state, because API doesn't respond with the current state.
    table.restore(current)
    return table
}
